import { refCountry } from "@tc/drizzle-v2"
import { eq, and, inArray } from "drizzle-orm"
import {
  MessageAttributeValue,
  SQSClient,
  SendMessageCommand,
} from "@aws-sdk/client-sqs"
import { DbTransaction } from "@tc/middy-decorators"
import { CountryDoc } from "../types/reference-data"
import { searchBuilderLog } from "../utils/logger"

const sqs_client = new SQSClient({})
const queue_url = process.env["COUNTRIES_QUEUE_URL"]

export const getCountriesData = async (
  tx: DbTransaction,
  id: string,
): Promise<CountryDoc | undefined> => {
  const [row] = await tx
    .select({
      countryCode: refCountry.itemCode,
      countryName: refCountry.itemName,
      subregion: refCountry.subregion,
      region: refCountry.region,
      shortNameEn: refCountry.short_name_en,
      active: refCountry.active,
    })
    .from(refCountry)
    .where(and(eq(refCountry.id, parseInt(id)), eq(refCountry.active, true)))

  if (!row) {
    console.error(`Country with id ${id} not found/active`)
    return
  }

  const countryDoc: CountryDoc = {
    id: row.countryCode ?? "",
    alpha_2_code: row.countryCode ?? "",
    label: row.countryName ?? "",
    name: row.countryName ?? "",
    subregion: row.subregion ?? "",
    region: row.region ?? "",
    short_name_en: row.shortNameEn ?? row.countryName ?? "",
    active: row.active ?? false,
  }

  searchBuilderLog.debug({ document: countryDoc })

  return countryDoc
}

export const saveCountriesToSearch = async (
  tx: DbTransaction,
  id: string,
  collectionName: string,
  options?: { returnDoc?: boolean },
): Promise<void> => {
  const doc = await getCountriesData(tx, id)

  if (!doc || Object.keys(doc).length === 0) {
    console.error(`Failed to prepare document for id: ${id}`)
    return
  }

  if (options?.returnDoc) return doc as never

  const attributes: Record<string, MessageAttributeValue> = {
    collection: {
      StringValue: collectionName,
      DataType: "String",
    },
  }

  const command = new SendMessageCommand({
    QueueUrl: queue_url,
    MessageBody: JSON.stringify(doc),
    MessageAttributes: attributes,
  })

  await sqs_client.send(command)
}

/** Bulk fetch — one query for a chunk of IDs, same row→doc mapping as getCountriesData */
export const getCountriesBulkData = async (
  tx: DbTransaction,
  ids: readonly string[],
): Promise<CountryDoc[]> => {
  const rows = await tx
    .select({
      countryCode: refCountry.itemCode,
      countryName: refCountry.itemName,
      subregion: refCountry.subregion,
      region: refCountry.region,
      shortNameEn: refCountry.short_name_en,
      active: refCountry.active,
    })
    .from(refCountry)
    .where(inArray(refCountry.id, ids.map(Number)))

  return rows
    .filter((r) => r.active)
    .map((r) => ({
      id: r.countryCode ?? "",
      alpha_2_code: r.countryCode ?? "",
      label: r.countryName ?? "",
      name: r.countryName ?? "",
      subregion: r.subregion ?? "",
      region: r.region ?? "",
      short_name_en: r.shortNameEn ?? r.countryName ?? "",
      active: r.active ?? true,
    }))
}
